import type { ReviewPrescoreProps } from './ReviewPrescore.types';
import type { Section } from 'features/sessions/types';

import React, { useMemo } from 'react';
import classNames from 'classnames';
import { useSelector } from 'react-redux';
import * as selectors from 'features/sessions/selectors';
import './_review-prescore.scss';

// Pearson VUE titles each prescore by a short subtest label, not the full
// section name (e.g. "Verbal Review Prescore", not "Verbal Reasoning ...").
const SECTION_LABEL: Record<Section['type'], string> = {
  VR: 'Verbal',
  DM: 'Decision Making',
  QR: 'Quantitative',
  AR: 'Abstract',
  SJ: 'SJT',
};

export const ReviewPrescore = ({ onReviewQuestion, onFlagToggleQuestion }: ReviewPrescoreProps) => {
  const currentSection = useSelector(selectors.selectCurrentSection);

  const title = useMemo(() => {
    const label = currentSection ? SECTION_LABEL[currentSection.type] : '';
    return `${label} Review Prescore: use this screen to review the items and amend your responses`;
  }, [currentSection]);

  // Pearson buckets Unseen and Incomplete together ("N Unseen/Incomplete").
  const pendingCount = useMemo(
    () => currentSection?.questions.filter((q) => q.status !== 'Completed').length ?? 0,
    [currentSection],
  );

  if (!currentSection) return null;

  return (
    <div className="review-prescore__container">
      <h5 className="review-prescore__title">{title}</h5>

      <div className="review-prescore__section-bar">
        <span>{currentSection.name} Section</span>
        <span>{`( ${pendingCount} Unseen/Incomplete )`}</span>
      </div>

      <div className="review-prescore__list">
        {currentSection.questions.map((question, index) => {
          const flagged = Boolean(question.flagged);
          return (
            <div className="review-prescore__row" key={question.id}>
              <button
                type="button"
                className={classNames('review-prescore__flag', {
                  'review-prescore__flag--on': flagged,
                })}
                aria-label={`${flagged ? 'Unflag' : 'Flag'} Question ${index + 1}`}
                aria-pressed={flagged}
                onClick={() => onFlagToggleQuestion(question.id, flagged)}
              />
              <button
                type="button"
                className="review-prescore__item-link"
                onClick={() => onReviewQuestion(question.id)}
              >
                {`Question ${index + 1}`}
              </button>
              <span
                className={classNames('review-prescore__status', {
                  'review-prescore__status--pending':
                    question.status === 'InComplete' || question.status === 'UnSeen',
                })}
              >
                {(question.status === 'InComplete' || question.status === 'UnSeen') && question.status}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
};
